import React, { useState, useEffect, useMemo,useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import toast from '../utils/toast';
import { API_BASE_URL, api } from '../config/api'; 
import { 
  FileText, Save, Printer, Plus, Trash2, Edit, Search, ArrowLeft,ArrowUpRight,Calendar,
  Activity, Upload, Loader2 
} from 'lucide-react';
import { formatDisplayDate, normalizeDateInput, toDateInputValue } from '../utils/dateFormat';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton,
  masterHeader,
  masterHeaderCard,
  masterHeaderIcon,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterPanelPadding,
  masterPrimaryButton,
  masterSearchInput,
  masterSearchWrap,
  masterSelect,
  masterSubtitle,
  masterToolbar,
  masterTitle,
  masterTableData
} from '../components/masterPageStyles';

const Prescription = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tab, setTab] = useState('digital');
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]); 
  const [patients, setPatients] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [stockList, setStockList] = useState([]); 
  const [medicineMaster, setMedicineMaster] = useState([]); 
  const [schedules, setSchedules] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nextId, setNextId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [doctorSearch, setDoctorSearch] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [labTestFilter, setLabTestFilter] = useState('all'); // 'all', 'yes', 'no'
const [statusFilter, setStatusFilter] = useState('all');   // 'all', 'pending', 'completed'

  const initialFormData = {
    doctorId: '',
    patientId: '',
    appointmentId: '',
    visitDate: toDateInputValue(new Date()),
    vitals: { bp: '', weight: '', temp: '', sugar: '' },
    clinical: { symptoms: '', diagnosis: '', advice: '' },
    medicines: [{ medicine_id: '', name: '', dosage: '', frequency: '', duration: '', instructions: '',
    schedule: {} }],
    uploadUrl: '', 
    files: [],     
    description: '',
    nextVisitDate: '',
    labTest: false,
  };

  const [formData, setFormData] = useState(initialFormData);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [drRes, apptRes, patientRes, prRes, stockRes, medRes, schedRes] = await Promise.all([
        api.get('/doctors/all'),
        api.get('/appointments/all'), 
        api.get('/patients/all'),
        api.get('/prescriptions/all'),
        api.get('/stock'),
        api.get('/master/medicine/all'),
        api.get('/schedules/all').catch(() => api.get('/appointments/schedules')).catch(() => ({ data: { success: false } }))
      ]);
      if (drRes.data.success) setDoctors(drRes.data.data || []);
      if (apptRes.data.success) setAppointments(apptRes.data.data || []);
      if (patientRes.data.success) setPatients(patientRes.data.data || []);
      if (prRes.data.success) setPrescriptions(prRes.data.data || []);
      if (schedRes && schedRes.data?.success) setSchedules(schedRes.data.data || []);
      
      const incomingStock = Array.isArray(stockRes.data?.data) 
        ? stockRes.data.data 
        : Array.isArray(stockRes.data) ? stockRes.data : [];
      setStockList(incomingStock);

      const incomingMeds = Array.isArray(medRes.data?.data) 
        ? medRes.data.data 
        : Array.isArray(medRes.data) ? medRes.data : [];
      setMedicineMaster(incomingMeds);
    } catch (err) {
      toast.error('FAILED TO LOAD DATA');
    } finally {
      setLoading(false);
    }
  };

  const medicineMap = useMemo(() => {
    const map = new Map();
    medicineMaster.forEach((med) => {
      if (med?.medicine_id) map.set(String(med.medicine_id), med);
    });
    return map;
  }, [medicineMaster]);


  const handleSendLabReminder = async (id) => {
    try {
      const res = await api.post(`/prescriptions/lab-reminder/${id}`);
      if (res.data.success) {
        toast.success("LAB REMINDER SENT!");
      }
    } catch (err) {
      toast.error("FAILED TO SEND");
    }
  };

  const doctorOptions = useMemo(() => {
    return doctors
      .map((doc) => ({
        value: doc.doctorId || doc._id,
        label: doc.doctorName || '',
        search: `${doc.doctorName} ${doc.doctorId}`.toLowerCase(),
        doc
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [doctors]);

const bookedPatients = useMemo(() => {
    if (!formData.doctorId) return [];

    const selectedDoctor = doctors.find((doc) => doc.doctorId === formData.doctorId);
    if (!selectedDoctor?.doctorId) return [];

    // Filter appointments for the selected doctor
    const filteredAppointments = appointments.filter(
      (appt) => appt.doctorId === selectedDoctor.doctorId
    );

    // Map directly from appointments instead of patients collection
    return filteredAppointments.map((appt) => ({
      id: appt.patientId || appt._id,
      name: appt.patientName || 'UNKNOWN',
      phone: appt.patientMobile || '',
      appointmentId: appt.appointmentId || ''
    }));
  }, [formData.doctorId, doctors, appointments]);

  const patientOptions = useMemo(() => {
    return bookedPatients
      .map((p) => ({
        value: p.id,
        label: p.name,
        phone: p.phone,
        search: `${p.name} ${p.id} ${p.phone}`.toLowerCase(),
        p
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [bookedPatients]);

  const availableMedicineOptions = useMemo(() => {
    return stockList
      .filter((stock) => {
        const stockQty = Number(stock.total_stock_available ?? stock.total_grn_received_quantity ?? 0);
        return stockQty > 0;
      })
      .map((stock) => {
        const matchedMedicineModel = medicineMap.get(String(stock.medicine_id));
        const resolvedMedicineName = matchedMedicineModel?.medicine_name || stock.medicine_id;
        return {
          value: stock.medicine_id,
          label: String(resolvedMedicineName).toUpperCase(),
          search: String(resolvedMedicineName).toLowerCase()
        };
      })
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [stockList, medicineMap]);

  const doctorMap = useMemo(() => {
    return doctors.reduce((acc, doc) => {
      acc[doc.doctorId] = doc;
      return acc;
    }, {});
  }, [doctors]);

  const patientMap = useMemo(() => {
    return patients.reduce((acc, p) => {
      acc[p.patientId] = p;
      return acc;
    }, {});
  }, [patients]);

  useEffect(() => {
    fetchData();
  }, []);

  // Multi-file convert configuration directly to Base64 strings format strings array maps
  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    setUploading(true);
    try {
      const filePromises = selectedFiles.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve({ name: file.name, data: reader.result });
          reader.onerror = (error) => reject(error);
        });
      });

      const convertedFiles = await Promise.all(filePromises);
      
      setFormData((prev) => ({
        ...prev,
        files: [...(prev.files || []), ...convertedFiles]
      }));
      
      toast.success(`${convertedFiles.length} file(s) processed locally`);
    } catch (err) {
      toast.error('Failed to parse uploads');
    } finally {
      setUploading(false);
    }
  };

  const addMedicine = () => {
    setFormData({
      ...formData,
      medicines: [...formData.medicines, { name: '', dosage: '', frequency: '', duration: '',instructions: '',
      schedule: {} }]
    });
  };

  const handleClose = async (id) => {
    if (!window.confirm("Close this prescription record?")) return;
    try {
      const res = await api.patch(`/prescriptions/close/${id}`);
      if (res.data.success) {
        toast.success("CLOSED");
        fetchData();
      }
    } catch (err) { toast.error("CLOSE FAILED"); }
  };
  const removeMedicine = (index) => {
    const list = [...formData.medicines];
    list.splice(index, 1);
    setFormData({ ...formData, medicines: list });
  };

const handleEdit = (item) => {
  setEditingId(item._id);
  setTab(item.type?.toLowerCase() === 'handwritten' ? 'handwritten' : 'digital');
  setNextId(item.prescriptionId);
  
  const currentDr = doctors.find(d => d.doctorId === item.doctor);
  const currentPt = patients.find(p => p.patientId === item.patient);
  
  setDoctorSearch(currentDr?.doctorName || item.doctor || '');
  setPatientSearch(currentPt?.fullName || item.patient || '');
  setPhoneNumber(currentPt?.mobileNumber || '');

  setFormData({
    doctorId: item.doctor || '',
    patientId: item.patient || '',
    visitDate: toDateInputValue(item.visitDate || new Date()),
    vitals: item.vitals || initialFormData.vitals,
    clinical: item.clinical || initialFormData.clinical,
    // --- UPDATED MEDICINES MAPPING ---
    medicines: (item.medicines || initialFormData.medicines).map(m => {
      let schedule = {};
      try {
        // Parse the JSON string stored in 'instructions' back into an object
        schedule = (m.instructions && m.instructions.startsWith('{')) 
          ? JSON.parse(m.instructions) 
          : {};
      } catch(e) { 
        schedule = {}; 
      }
      return {
        ...m,
        schedule: schedule // Assign the parsed object to the new 'schedule' field
      };
    }),
    // ---------------------------------
    uploadUrl: item.uploadUrl || '',
    files: item.files || [],
    description: item.description || '',
    nextVisitDate: item.nextVisitDate ? String(item.nextVisitDate).split('T')[0] : '',
    labTest: Boolean(item.labTest)
  });
  setShowForm(true);
};

  const handlePrint = (item) => {
    const win = window.open('', '_blank', 'width=950,height=750');
    if (!win) return;

    const drObj = doctorMap[item.doctor] || {};
    const ptObj = patientMap[item.patient] || {};

    const doctorName = drObj.doctorName || item.doctor || '-';
    const patientName = ptObj.fullName || item.patient || '-';
    const patientAge = ptObj.age ?? '-';
    const patientGender = ptObj.gender || '-';

    const drSpecialization = drObj.department || 'GENERAL';
    const drDegree = drObj.qualification || '';
    const drRegNo = drObj.registrationNumber || '-';
    const drPhone = drObj.mobile || '-';

    const doctorScheduleObj = schedules.find(s => s.doctorId === item.doctor);
    let resolvedHours = 'On Appointment';
    if (doctorScheduleObj) {
      const activeDays = Array.isArray(doctorScheduleObj.days) ? doctorScheduleObj.days.join(', ') : '';
      resolvedHours = `${activeDays} ${doctorScheduleObj.startTime || ''} - ${doctorScheduleObj.endTime || ''}`;
    }

    const isHandwritten = String(item.type || '').toLowerCase() === 'handwritten';

  const medicinesHtml = (item.medicines || [])
  .map((m, idx) => {
  const formatInstructions = (instr) => {
  try {
    const sched = JSON.parse(instr);
    return Object.entries(sched)
      .map(([time, data]) => {
        // Ensure data is uppercase
        const unit = String(data.unit || '').toUpperCase();
        const qty = String(data.qty || '');
        const meal = String(data.meal || '').toUpperCase();
        
        // This will look like: M : TAB 1 BM
        return `<strong>${time}</strong> : ${unit} ${qty} ${meal}`;
      })
      // Use <br/> to force a new line for each time slot
      .join('<br/>'); 
  } catch (e) { 
    return instr ? String(instr).toUpperCase() : '-'; 
  }
};

    return `
      <tr>
        <td style="padding: 5px 8px; border-bottom: 1px solid #eee;">${idx + 1}</td>
        <td style="padding: 5px 8px; border-bottom: 1px solid #eee; font-weight: bold; text-transform: uppercase;">${m.name || '-'}</td>
        <td style="padding: 5px 8px; border-bottom: 1px solid #eee;">${m.dosage || '-'}</td>
        <td style="padding: 5px 8px; border-bottom: 1px solid #eee;">${m.frequency || '-'}</td>
        <td style="padding: 5px 8px; border-bottom: 1px solid #eee;">${m.duration || '-'}</td>
        <td style="padding: 5px 8px; border-bottom: 1px solid #eee; color: #555;">${formatInstructions(m.instructions)}</td>
      </tr>
    `;
  })
  .join('');

    const visitDate = item.visitDate ? formatDisplayDate(item.visitDate) : '-';
    const nextVisitDate = item.nextVisitDate ? formatDisplayDate(item.nextVisitDate) : '-';

    // Generates output loops for multiple Base64 rendering items
    const attachmentFilesMarkup = (item.files || [])
      .map(file => {
        if (String(file.data || '').startsWith('data:application/pdf')) {
          return `<div style="margin-top:15px;"><iframe src="${file.data}" style="width:100%; height:500px; border:1px solid #ccc;"></iframe><p style="font-size:10px; text-align:center;">${file.name}</p></div>`;
        }
        return `<div style="text-align: center; margin-top: 15px;"><img src="${file.data}" style="max-width: 100%; max-height: 480px; object-fit: contain; border: 1px solid #ccc;" /><p style="font-size:10px; color:#666;">${file.name}</p></div>`;
      })
      .join('');

    win.document.write(`
      <html>
        <head>
          <title>Prescription Pad</title>
          <style>
            @page { size: A4; margin: 6mm 0mm; }
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #222; margin: 0; padding: 0 35px; background: #fff; }
            .pad-header { display: flex; justify-content: space-between; border-bottom: 2px solid #000; padding-bottom: 6px; margin-bottom: 10px; }
            .left-brand { width: 45%; text-align: left; }
            .brand-title { font-size: 24px; font-weight: 900; color: #1e3a8a; margin: 0; letter-spacing: 0.3px; text-transform: uppercase; }
            .brand-subtitle { font-size: 11px; font-weight: bold; color: #000; margin: 1px 0 4px; letter-spacing: 0.5px; text-transform: uppercase; }
            .brand-motto { font-style: italic; font-size: 12px; color: #555; margin-bottom: 4px; }
            .brand-contact { font-size: 10px; color: #333; line-height: 1.35; margin: 0; font-weight: 600; }
            .right-doctor { width: 53%; text-align: right; font-size: 11px; line-height: 1.4; color: #333; }
            .doctor-name { font-size: 22px; font-family: Georgia, serif; color: #047857; margin: 0 0 1px; font-weight: bold; text-transform: uppercase; }
            .doctor-spec { font-weight: bold; font-size: 11px; text-transform: uppercase; color: #444; margin: 0 0 1px; }
            .doctor-deg { color: #555; font-weight: 600; margin: 0 0 1px; }
            .doctor-meta { font-weight: 600; color: #222; margin: 1px 0; }
            .patient-bar { display: flex; flex-wrap: wrap; justify-content: space-between; border-bottom: 1px dotted #000; padding: 5px 0; margin-bottom: 15px; font-size: 12px; font-weight: bold; }
            .patient-bar span { margin-right: 10px; }
            .prescription-body { display: flex; min-height: 480px; }
            .vitals-sidebar { width: 25%; border-right: 1px solid #ddd; padding-right: 12px; font-size: 11px; }
            .main-content { width: 75%; padding-left: 15px; }
            .section-title { font-size: 11px; font-weight: bold; text-transform: uppercase; color: #1e3a8a; border-bottom: 1px solid #1e3a8a; padding-bottom: 1px; margin: 10px 0 4px; }
            .vital-row { display: flex; justify-content: space-between; padding: 3px 0; border-bottom: 1px dashed #eee; font-weight: 600; }
            table { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 11px; }
            th { text-align: left; background: #f8fafc; padding: 6px 8px; font-weight: bold; border-bottom: 2px solid #cbd5e1; color: #1e3a8a; }
            .footer-notes { margin-top: 20px; border-top: 1px solid #ddd; padding-top: 8px; font-size: 11px; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="pad-header">
            <div class="left-brand">
              <h1 class="brand-title">Asha The Hope</h1>
              <div class="brand-subtitle">Diagnostic & Imaging LLP.</div>
              <div class="brand-motto">Diagnosis at it's best</div>
              <p class="brand-contact">
                33, J. N. Tewari Road, Dum Dum Cantonment<br/>
                (Near Dum Dum Municipal Hospital), Kolkata - 700028<br/>
                Phone : (033) 2529-4005/4006, Mob. : 94323 07066<br/>
                E-mail : ashathehope2016@gmail.com<br/>
                Website : www.ashathehope.co.in
              </p>
            </div>
            
            <div class="right-doctor">
              <h2 class="doctor-name">DR. ${doctorName}</h2>
              ${drSpecialization ? `<div class="doctor-spec">(${drSpecialization})</div>` : ''}
              ${drDegree ? `<div class="doctor-deg">${drDegree}</div>` : ''}
              <div class="doctor-meta">Registration No. : ${drRegNo}</div>
              <div class="doctor-meta">Mob. : ${drPhone}</div>
              <div style="margin-top: 3px; font-weight: bold; color: #047857; text-transform: uppercase;">
                Schedule: ${resolvedHours}
              </div>
            </div>
          </div>

          <div class="patient-bar">
            <div>P. Name: <span style="font-weight: normal; text-transform: uppercase;">${patientName}</span></div>
            <div>AGE/SEX: <span style="font-weight: normal; text-transform: uppercase;">${patientAge} / ${patientGender}</span></div>
            <div>BP: <span style="font-weight: normal;">${item.vitals?.bp || '_____'}</span></div>
            <div>WT: <span style="font-weight: normal;">${item.vitals?.weight ? item.vitals.weight + ' Kg' : '_____'}</span></div>
            <div>Date: <span style="font-weight: normal;">${visitDate}</span></div>
          </div>

          ${
            isHandwritten
              ? `
                ${item.description ? `<div style="margin-top: 15px;"><strong>Notes:</strong><p style="font-size: 12.5px; line-height: 1.45;">${item.description}</p></div>` : ''}
                ${attachmentFilesMarkup || '<div style="margin-top: 15px; color: #999;">No prescription files loaded.</div>'}
              `
              : `
                <div class="prescription-body">
                  <div class="vitals-sidebar">
                    <div class="section-title">Vitals Report</div>
                    <div class="vital-row"><span>Blood Pressure:</span> <span>${item.vitals?.bp || '-'}</span></div>
                    <div class="vital-row"><span>Weight:</span> <span>${item.vitals?.weight || '-'}</span></div>
                    <div class="vital-row"><span>Temperature:</span> <span>${item.vitals?.temp || '-'}</span></div>
                    <div class="vital-row"><span>Blood Sugar:</span> <span>${item.vitals?.sugar || '-'}</span></div>
                    
                    ${item.clinical?.symptoms ? `
                      <div class="section-title">Symptoms</div>
                      <p style="margin: 3px 0; line-height: 1.35; color: #444;">${item.clinical.symptoms}</p>
                    ` : ''}
                  </div>
                  
                  <div class="main-content">
                    ${item.clinical?.diagnosis ? `
                      <div class="section-title">Diagnosis</div>
                      <p style="margin: 3px 0; font-size: 11.5px; font-weight: bold; color: #333;">${item.clinical.diagnosis}</p>
                    ` : ''}
                    
                    <div class="section-title" style="font-size: 14px; padding-bottom: 3px; margin-top: 15px;">Rx (Medicines)</div>
                    <table>
                      <thead>
                        <tr>
                          <th style="width: 5%">#</th>
                          <th style="width: 35%">Medicine Name</th>
                          <th style="width: 12%">Dosage</th>
                          <th style="width: 12%">Frequency</th>
                          <th style="width: 12%">No of Medicine</th>
                          <th style="width: 24%">Instructions</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${medicinesHtml || '<tr><td colspan="6" style="padding: 8px; text-align: center; color: #999;">No medicines selected.</td></tr>'}
                      </tbody>
                    </table>

                    ${item.clinical?.advice ? `
                      <div class="section-title" style="margin-top: 20px;">Advice</div>
                      <p style="margin: 3px 0; line-height: 1.4; color: #333; font-size: 11px;">${item.clinical.advice}</p>
                    ` : ''}
                  </div>
                </div>
              `
          }

          <div class="footer-notes">
            <div>Next Follow-up Date: ${nextVisitDate}</div>
          </div>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
    }, 300);
  };

 const handleSave = async (actionType) => {
    if (!formData.doctorId) return toast.error('SELECT DOCTOR');
    if (!formData.patientId) return toast.error('SELECT PATIENT');
    if (!formData.visitDate) return toast.error('ENTER VISIT DATE');
    setSaving(true);
    try {
      //  MODIFIED PAYLOAD: Specifically maps the medicines array 
      // to prioritize medicine_id instead of just the name.
      const payload = {
        ...formData,
        doctor: formData.doctorId,
        patient: formData.patientId,
        patientName: patientSearch,       // <-- Ensure this is included here
        appointmentId: formData.appointmentId,
        patientPhone: phoneNumber, 
        type: tab === 'digital' ? 'Digital' : 'Handwritten',
        prescriptionId: nextId,
        medicines: formData.medicines.map((m) => ({
          medicine_id: m.medicine_id || '', // This will store "ANTIBIOTICS-2"
          name: m.name,                     // Kept for UI reference/display
          dosage: m.dosage,
          frequency: m.frequency,
          duration: m.duration,
          instructions: JSON.stringify(m.schedule || {})
        }))
      };

      const res = editingId 
        ? await api.put(`/prescriptions/update/${editingId}`, payload)
        : await api.post('/prescriptions/add', payload);

      if (res.data.success) {
        toast.success(editingId ? "UPDATED" : "SAVED");
        
        if (actionType === 'print') {
          handlePrint(res.data.data || payload);
        }

        setShowForm(true); 
        setEditingId(null);
        setFormData(initialFormData);
        setDoctorSearch('');
        setPatientSearch('');
        setPhoneNumber('');
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "SAVE FAILED");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      const res = await api.delete(`/prescriptions/${id}`);
      if (res.data.success) {
        toast.success("DELETED");
        fetchData();
      }
    } catch (err) { toast.error("DELETE FAILED"); }
  };

const filteredPrescriptions = useMemo(() => {
  const sortedList = [...prescriptions].sort((a, b) => {
    const idA = String(a.prescriptionId || a._id || '');
    const idB = String(b.prescriptionId || b._id || '');
    return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
  });

  return sortedList.filter((item) => {
    // 1. Search filter
    const q = (searchTerm || '').trim().toLowerCase();
    const presId = (item.prescriptionId || '').toLowerCase();
    const patientId = (item.patient || '').toLowerCase();
    const patientName = (item.patientName || patientMap[item.patient]?.fullName || '').toLowerCase();
    const doctorId = (item.doctor || '').toLowerCase();
    const doctorName = (doctorMap[item.doctor]?.doctorName || '').toLowerCase();

    const matchesSearch = !q || (
      presId.startsWith(q) ||
      patientId.startsWith(q) ||
      patientName.startsWith(q) ||
      doctorId.startsWith(q) ||
      doctorName.startsWith(q)
    );

    // 2. Lab Test filter
    const matchesLab = 
      labTestFilter === 'all' ? true :
      labTestFilter === 'yes' ? Boolean(item.labTest) : !item.labTest;

    // 3. Status filter (Pending = closing false/undefined, Completed = closing true)
    const matchesStatus = 
      statusFilter === 'all' ? true :
      statusFilter === 'completed' ? Boolean(item.closing) : !item.closing;

    return matchesSearch && matchesLab && matchesStatus;
  });
}, [prescriptions, searchTerm, labTestFilter, statusFilter, doctorMap, patientMap]);

  const paginatedPrescriptions = useMemo(() => {
    if (pageSize === -1) return filteredPrescriptions;
    const start = (currentPage - 1) * pageSize;
    return filteredPrescriptions.slice(start, start + pageSize);
  }, [filteredPrescriptions, currentPage, pageSize]);

  return (
    <div className={masterPageShell}>
    <header className={masterHeader}>
  <div className={`${masterHeaderCard} max-w-6xl flex items-center justify-between`}>
    <div className="flex items-center gap-3">
      <div className={masterHeaderIcon}><FileText size={18} /></div>
      <div>
        <h1 className={masterTitle}>Prescriptions</h1>
        <p className={masterSubtitle}>Digital + Upload</p>
      </div>
    </div>

    {/* Right side buttons container */}
    <div className="flex items-center gap-2">
      {/* Go to Appointment Management Button */}
      <button 
        type="button"
        onClick={() => navigate('/AppointmentManagement')}
        className="px-3 py-1 text-[10px] font-black text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm flex items-center gap-1.5 transition"
      >
        <Calendar size={14} /> 
        <span>GO TO APPOINTMENT MANAGE</span>
        <ArrowUpRight size={14} />
      </button>

      {/* Existing Form Toggle Button */}
      <button 
        onClick={() => { 
          if(!showForm) { 
            setEditingId(null); 
            setFormData(initialFormData); 
            setDoctorSearch('');
            setPatientSearch('');
            setPhoneNumber('');
          } 
          setShowForm(!showForm); 
        }} 
        className={showForm ? masterPrimaryButton : masterAccentButton}
      >
        {showForm ? <ArrowLeft size={14} /> : <Plus size={14} />}
        {showForm ? 'VIEW PRESCRIPTION LOGS' : 'OPEN PRESCRIPTION FORM'}
      </button>
    </div>
  </div>
</header>

      <main className="px-1 pb-1">
      <div className="mx-auto max-w-6xl rounded-xl border border-slate-400 bg-white shadow-sm relative overflow-hidden">
       {showForm && (
  <div className="sticky top-0 z-30 flex bg-slate-900 p-1 shadow-md gap-1">
    <button onClick={() => setShowForm(false)} className="px-1 text-white hover:bg-slate-800 rounded-lg transition">
      <ArrowLeft size={18}/>
    </button>
    <button 
      onClick={() => setTab('digital')}
      className={`flex-1 flex items-center justify-center gap-1 py-1 rounded-xl text-[10px] font-bold uppercase tracking-widest transition ${tab === 'digital' ? 'bg-emerald-500 text-white' : 'text-emerald-600 hover:text-white'}`}
    >
      <Activity size={16} /> Digital
    </button>
    <button 
      onClick={() => setTab('handwritten')}
      className={`flex-1 flex items-center justify-center gap-1 py-1 rounded-xl text-[10px] font-bold uppercase tracking-widest transition ${tab === 'handwritten' ? 'bg-emerald-500 text-white' : 'text-emerald-600 hover:text-white'}`}
    >
      <Upload size={16} /> Upload
    </button>
  </div>
)}
        <div className="p-1 space-y-1">
          
      {!showForm ? (
            <div className="max-w-7xl mx-auto bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
              {/* Top Bar with Filters, Search, and Add Button */}
              <div className="border-b border-slate-200 flex-shrink-0 flex flex-col md:flex-row items-center justify-between gap-2 p-2 bg-slate-50">
                <div className="flex items-center gap-2 w-full md:w-auto">
                  <select
                    value={labTestFilter}
                    onChange={(e) => { setLabTestFilter(e.target.value); setCurrentPage(1); }}
                    className="border border-slate-300 rounded-lg px-2 py-1.5 text-[10px] font-bold uppercase bg-white text-slate-700 outline-none"
                  >
                    <option value="all">Lab: All</option>
                    <option value="yes"> Yes</option>
                    <option value="no">No</option>
                  </select>

                  <select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                    className="border border-slate-300 rounded-lg px-2 py-1.5 text-[10px] font-bold uppercase bg-white text-slate-700 outline-none"
                  >
                    <option value="all">Status: All</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                <div className="flex-1 w-full">
                  <SearchBar 
                    value={searchTerm} 
                    onChange={(val) => {
                      setSearchTerm(val);
                      setCurrentPage(1);
                    }} 
                    placeholder="Search alphabetically by prescription ID, patient, or doctor..." 
                    totalItems={filteredPrescriptions.length} 
                    currentPage={currentPage} 
                    pageSize={pageSize} 
                    onPageChange={setCurrentPage} 
                    onPageSizeChange={setPageSize} 
                  />
                </div>

                {/* <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setFormData(initialFormData);
                    setDoctorSearch('');
                    setPatientSearch('');
                    setPhoneNumber('');
                    setShowForm(true);
                  }}
                  className={`${masterPrimaryButton} px-3 py-1 text-[10px] flex items-center gap-1 flex-shrink-0`}
                >
                  <Plus size={14} /> ADD PRESCRIPTION
                </button> */}
              </div>

              {/* Prescription Table */}
              <div className="max-h-[calc(100vh-250px)] overflow-auto">
                <table className="w-full min-w-[1100px] table-fixed text-left text-[10px]">
                  <thead className="sticky top-0 z-10 bg-slate-100 text-[9px] font-extrabold uppercase tracking-[0.14em] text-emerald-600 border-b border-slate-400">
                    <tr>
                      <th className={masterTableData}>S.No</th>
                      <th className={masterTableData}>ID</th>
                      <th className={masterTableData}>Patient</th>
                      <th className={masterTableData}>Doctor</th>
                      <th className={masterTableData}>Lab Test</th>
                      <th className={masterTableData}>Follow-Up</th>
                      <th className={masterTableData}>Closing</th>
                      <th className={masterTableData}>Lab Reminder</th>
                      <th className={masterTableData}>Actions</th>
                    </tr>
                  </thead>
               <tbody className="divide-y divide-slate-300 font-bold text-[11px] text-slate-800">
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="p-12 text-center text-slate-500 font-bold uppercase text-xs">
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="animate-spin text-emerald-600" size={16} />
                          <span>Please wait, loading records...</span>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedPrescriptions.length > 0 ? (
                    paginatedPrescriptions.map((item, index) => (
                      <tr key={item._id} className="hover:bg-emerald-50/30">
                        <td className={masterTableData}>{pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}</td>
                        <td className={masterTableData}>{item.prescriptionId}</td>
                        <td className={masterTableData}>{item.patientName || patientMap[item.patient]?.fullName || item.patient || '-'}</td>
                        <td className={masterTableData}>{doctorMap[item.doctor]?.doctorName || item.doctor || '-'}</td>
                        <td className={masterTableData}>
                          <span className={`px-1 py-0.5 rounded text-[9px] ${item.labTest ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                            {item.labTest ? 'YES' : 'NO'}
                          </span>
                        </td>
                        <td className={masterTableData}>
                          {item.nextVisitDate ? formatDisplayDate(item.nextVisitDate) : '-'}
                        </td>
                       <td className={masterTableData}>
                          {item.labTest ? (
                            item.closing ? (
                              <span className="px-2 py-1 text-[9px] font-black text-emerald-700 bg-emerald-100 rounded">
                                COMPLETED
                              </span>
                            ) : (
                              <button 
                                type="button"
                                onClick={() => handleClose(item._id)} 
                                className="px-2 py-1 text-[9px] font-black text-white bg-rose-500 hover:bg-rose-600 rounded shadow-sm"
                              >
                                CLOSE
                              </button>
                            )
                          ) : (
                            <span className="text-slate-400 font-normal">N/A</span>
                          )}
                        </td>
                        <td className={masterTableData}>
                          {item.labTest ? (
                            <button 
                              type="button"
                              disabled={item.closing}
                              onClick={() => handleSendLabReminder(item._id)}
                              className={`px-2 py-1 text-[9px] font-black rounded shadow-sm ${
                                item.closing 
                                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed' 
                                  : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                              }`}
                            >
                              NOTIFY
                            </button>
                          ) : (
                            <span className="text-slate-400 font-normal">N/A</span>
                          )}
                        </td>
                        <td className={masterTableData}>
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => handlePrint(item)} className="p-1 text-blue-500 bg-blue-50 rounded-lg"><Printer size={14}/></button>
                            <button onClick={() => handleEdit(item)} className="p-1 text-emerald-500 bg-emerald-50 rounded-lg"><Edit size={14}/></button>
                            <button onClick={() => handleDelete(item._id)} className="p-1 text-rose-500 bg-rose-50 rounded-lg"><Trash2 size={14}/></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">No matching records found</td>
                    </tr>
                  )}
                </tbody>
                </table>
              </div>
            </div>
          ) : (
            // ... your existing form code ...
            <div className="space-y-1">
              <div className={`${masterPanelPadding} grid grid-cols-1 md:grid-cols-4 gap-3`}>
                  <div className="flex flex-col gap-1">
                    <SearchSelect
                      label="Doctor Lookup (Name / ID)"
                      value={doctorSearch}
                      onChange={(value) => {
                        setDoctorSearch(value);
                        setFormData({ ...formData, doctorId: '', patientId: '' });
                        setPatientSearch('');
                        setPhoneNumber('');
                      }}
                      onSelect={(option) => {
                        setDoctorSearch(option.label.toUpperCase());
                        setFormData({ ...formData, doctorId: option.value, patientId: '' });
                        setPatientSearch('');
                        setPhoneNumber('');
                      }}
                      options={doctorOptions}
                      placeholder="Type Name or Doctor ID..."
                      emptyText="No doctor found"
                      forceOpenOnFocus
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <SearchSelect
                      label="Patient Lookup (Name / ID / Phone)"
                      value={patientSearch}
                      onChange={(value) => {
                        setPatientSearch(value);
                        setFormData({ ...formData, patientId: '' });
                        setPhoneNumber('');
                      }}
                      disabled={!formData.doctorId}
                      onSelect={(option) => {
                        setPatientSearch(option.label.toUpperCase());
                        setFormData({ ...formData, patientId: option.value });
                        setPhoneNumber(option.phone || 'N/A');
                      }}
                      options={patientOptions}
                      placeholder={formData.doctorId ? "Type Name, ID, or Phone..." : "Select Doctor First"}
                      emptyText="No booked patients found"
                      forceOpenOnFocus
                      renderOption={(option) => (
                        <div className="flex flex-col text-left py-0.5">
                          <span className="font-extrabold uppercase text-slate-800">{option.label}</span>
                          <span className="text-[9px] font-bold text-emerald-600">
                            Phone: {option.phone || 'N/A'}
                          </span>
                        </div>
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={masterInputLabel}>Patient Phone Number</label>
                    <input 
                      type="text" 
                      className={`${masterInput} bg-slate-100 text-slate-700 font-bold cursor-not-allowed`} 
                      value={phoneNumber} 
                      readOnly 
                      placeholder="Auto-populated phone"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={masterInputLabel}>Visit Date</label>
                    <input 
                      type="text"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="DD/MM/YYYY"
                      className={masterInput} 
                      value={formData.visitDate} 
                      onChange={(e) => setFormData({...formData, visitDate: normalizeDateInput(e.target.value)})}
                    />
                  </div>
              </div>

              {tab === 'digital' ? (
                <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 space-y-1">
                  <div className={`${masterPanelPadding} grid grid-cols-2 md:grid-cols-4 gap-1`}>
                    {['bp', 'weight', 'temp', 'sugar'].map(v => (
                      <div key={v} className="flex flex-col gap-1">
                        <label className={masterInputLabel}>{v}</label>
                        <input 
                          placeholder="---" 
                          className={masterInput}
                          value={formData.vitals[v]}
                          onChange={(e) => setFormData({...formData, vitals: {...formData.vitals, [v]: e.target.value}})}
                        />
                      </div>
                    ))}
                  </div>

                  <div className={`${masterPanelPadding} grid grid-cols-1 md:grid-cols-3 gap-1`}>
                    <div className="flex flex-col gap-1">
                       <label className={masterInputLabel}>Symptoms</label>
                       <textarea 
                        rows={2}
                        className={`${masterInput} resize-none`} 
                        value={formData.clinical.symptoms}
                        onChange={(e) => setFormData({...formData, clinical: {...formData.clinical, symptoms: e.target.value}})}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                       <label className={masterInputLabel}>Diagnosis</label>
                       <textarea 
                        rows={2}
                        className={`${masterInput} resize-none`} 
                        value={formData.clinical.diagnosis}
                        onChange={(e) => setFormData({...formData, clinical: {...formData.clinical, diagnosis: e.target.value}})}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                       <label className={masterInputLabel}>Advice</label>
                       <textarea 
                        rows={2}
                        className={`${masterInput} resize-none`} 
                        value={formData.clinical.advice}
                        onChange={(e) => setFormData({...formData, clinical: {...formData.clinical, advice: e.target.value}})}
                      />
                    </div>
                  </div>

                  <div className={masterPanelPadding}>
                    <div className="flex justify-between items-center mb-1">
                      <h3 className="text-[10px] font-black uppercase text-slate-700 flex items-center gap-2">Medicine List</h3>
                      <button type="button" onClick={addMedicine} className={masterAccentButton}>
                        <Plus size={12}/> ADD ROW
                      </button>
                    </div>
                  <div className="overflow-x-auto rounded-xl border border-slate-400">
                  {/* Added 'table-fixed' so the width percentages are strictly followed */}
                  <table className="w-full min-w-[800px] table-fixed text-[10px]">
                    <thead className="text-[9px] font-black uppercase text-emerald-600 border-b border-slate-400 bg-slate-100">
                      <tr>
                        <th className={masterTableData} style={{ width: '10%' }}>Medicine Name</th>
                        <th className={masterTableData} style={{ width: '10%' }}>Dosage</th>
                        <th className={masterTableData} style={{ width: '10%' }}>Freq</th>
                        <th className={masterTableData} style={{ width: '5%' }}>No Of Medicine</th>
                        {/* Instructions gets the remaining space */}
                        <th className={masterTableData} style={{ width: '40%' }}>Instructions</th>
                        <th className={masterTableData} style={{ width: '3%' }}>X</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-300">
                      {formData.medicines.map((med, idx) => (
                        <tr key={idx}>
                          <td className="p-1 align-top">
                            <SearchSelect
                              label="" hideLabel value={med.name}
                              onChange={(value) => {
                                const m = [...formData.medicines];
                                m[idx].name = value;
                                setFormData({ ...formData, medicines: m });
                              }}
                              onSelect={(option) => {
                                const m = [...formData.medicines];
                                m[idx].name = option.label;
                                m[idx].medicine_id = option.value;
                                setFormData({ ...formData, medicines: m });
                              }}
                              options={availableMedicineOptions}
                              placeholder="Search..."
                              inputClassName="px-1.5 py-1"
                            />
                          </td>
                          <td className="p-1 align-top"><input className={`${masterInput} px-1`} value={med.dosage} onChange={(e) => { const m = [...formData.medicines]; m[idx].dosage = e.target.value; setFormData({...formData, medicines: m}); }} /></td>
                          <td className="p-1 align-top"><input className={`${masterInput} px-1`} value={med.frequency} onChange={(e) => { const m = [...formData.medicines]; m[idx].frequency = e.target.value; setFormData({...formData, medicines: m}); }} /></td>
                          <td className="p-1 align-top"><input className={`${masterInput} px-1`} value={med.duration} onChange={(e) => { const m = [...formData.medicines]; m[idx].duration = e.target.value; setFormData({...formData, medicines: m}); }} /></td>
                          
                        {/* Instructions Cell: Responsive Flex Layout */}
                        <td className="p-1 align-top">
                          {/* Added flex-wrap and items-start to manage spacing */}
                          <div className="flex flex-wrap gap-1 items-start">
                            {['M', 'N', 'A', 'NT'].map((time) => (
                              // Changed w-[130px] to flex-1 to let it fit within the 40% column
                              <div key={time} className="flex flex-col items-center bg-slate-50 border border-slate-200 rounded p-0.5 min-w-[50px] flex-1">
                                
                                {/* Top: Checkbox + Time Label */}
                                <label className="flex items-center gap-0.5 cursor-pointer">
                                  <input 
                                    type="checkbox" 
                                    className="scale-75 "
                                    checked={!!med.schedule[time]}
                                    onChange={(e) => {
                                      const m = [...formData.medicines];
                                      if (e.target.checked) {
                                        m[idx].schedule[time] = { unit: 'Tab', qty: '1', meal: 'BM' };
                                      } else {
                                        delete m[idx].schedule[time];
                                      }
                                      setFormData({...formData, medicines: m});
                                    }}
                                  />
                                  <span className="font-black text-[9px] text-emerald-700">{time}</span>
                                </label>

                                {/* Bottom: Horizontal Controls */}
                                {med.schedule[time] && (
                                  <div className="flex w-full gap-0.5 mt-0.5">
                                  <select 
                                      className="text-[8px] border border-slate-300 rounded p-0 flex-1"
                                      value={med.schedule[time].unit}
                                      onChange={(e) => {
                                        const m = [...formData.medicines];
                                        m[idx].schedule[time].unit = e.target.value.toUpperCase(); // Force Uppercase
                                        setFormData({...formData, medicines: m});
                                      }}
                                    >
                                      <option value="TAB">Tab</option>
                                      <option value="ML">ml</option>
                                      <option value="TSP">tsp</option>
                                    </select>
                                    <input 
                                      type="number" 
                                      className="w-6 border border-slate-300 rounded text-[9px] text-center p-0 flex-1"
                                      value={med.schedule[time].qty}
                                      onChange={(e) => {
                                        const m = [...formData.medicines];
                                        m[idx].schedule[time].qty = e.target.value;
                                        setFormData({...formData, medicines: m});
                                      }}
                                    />
                                  <select 
                                    className="text-[8px] border border-slate-300 rounded p-0 flex-1"
                                    value={med.schedule[time].meal}
                                    onChange={(e) => {
                                      const m = [...formData.medicines];
                                      m[idx].schedule[time].meal = e.target.value.toUpperCase(); // Force Uppercase
                                      setFormData({...formData, medicines: m});
                                    }}
                                  >
                                    <option value="AM">AM</option>
                                    <option value="BM">BM</option>
                                  </select>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                          
                          <td className="p-1 text-center align-middle">
                            <button type="button" onClick={() => removeMedicine(idx)} className="text-rose-400 hover:text-rose-600">
                              <Trash2 size={14}/>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                  </div>
                </div>
              ) : (
                <div className="animate-in fade-in duration-500 space-y-1">
                  <div className={`${masterPanelPadding} border-2 border-dashed border-slate-300 text-center flex flex-col items-center gap-2 hover:border-emerald-300 transition relative`}>
                      <Upload size={24} className="text-slate-300" />
                      <input 
                        type="file" 
                        className="hidden" 
                        id="fileUp" 
                        accept="image/*,application/pdf" 
                        multiple 
                        onChange={handleFileChange} 
                      />
                      <label htmlFor="fileUp" className="cursor-pointer text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                        {uploading ? 'Processing Files...' : 'Click to upload multiple Prescription Files/PDFs'}
                      </label>
                  </div>

                  {/* Multiple file list output display mapped with filenames toggle */}
                  {formData.files && formData.files.length > 0 && (
                    <div className="p-2 border border-slate-200 bg-slate-50 rounded-lg space-y-1">
                      <div className="text-[9px] font-bold text-slate-500 uppercase">Attached Files:</div>
                      {formData.files.map((file, fIdx) => (
                        <div key={fIdx} className="flex items-center justify-between bg-white px-2 py-1 rounded border border-slate-300 text-[11px] font-bold text-slate-700">
                          <span className="truncate max-w-[80%]">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const updatedFiles = formData.files.filter((_, i) => i !== fIdx);
                              setFormData({ ...formData, files: updatedFiles });
                            }}
                            className="text-rose-500 hover:text-rose-700 p-0.5"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <textarea 
                    rows={2}
                    placeholder="Brief description or notes..." 
                    className={`${masterInput} w-full resize-none`} 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </div>
              )}

              <div className={`${masterPanelPadding} flex items-center justify-between pt-1 gap-1`}>
          
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-1">
                    <label className={masterInputLabel}>Follow-up Date</label>
                    <input 
                      type="date"
                      className={masterInput}
                      value={formData.nextVisitDate}
                      onChange={(e) => setFormData({...formData, nextVisitDate: e.target.value})}
                    />
                  </div>

                  {/* Added Lab Test Checkbox */}
                  <div className="flex items-center gap-1.5">
                    <input 
                      type="checkbox"
                      id="labTestCheckbox"
                      className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
                      checked={!!formData.labTest}
                      onChange={(e) => setFormData({...formData, labTest: e.target.checked})}
                    />
                    <label htmlFor="labTestCheckbox" className={`${masterInputLabel} cursor-pointer mb-0`}>
                      Lab Test
                    </label>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button 
                    type="button"
                    onClick={() => handleSave('save')} 
                    disabled={saving} 
                    className={`${masterAccentButton} px-2 py-1`}
                  >
                    {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={16} />} 
                    {editingId ? 'Update Only' : 'Save Only'}
                  </button>

                  <button 
                    type="button"
                    onClick={() => handleSave('print')} 
                    disabled={saving} 
                    className={`${masterPrimaryButton} bg-emerald-600 text-white hover:bg-emerald-700 px-2 py-1`}
                  >
                    {saving ? <Loader2 className="animate-spin" size={14}/> : <Printer size={16} />} 
                    {editingId ? 'Update & Print' : 'Save & Print'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </main>
    </div>
  );
};

const SearchSelect = ({
  label,
  value,
  onChange,
  onSelect,
  options,
  placeholder,
  renderOption,
  disabled = false,
  hideLabel = false,
  emptyText = 'No data found',
  forceOpenOnFocus = false
}) => {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const inputWrapRef = useRef(null);
  const normalized = String(value || '').trim().toLowerCase();

  const filteredOptions = useMemo(() => {
    if (!normalized) return options.slice(0, 30);
    return options
      .filter((option) => {
        const searchToken = String(option.search || '').toLowerCase();
        const labelStr = String(option.label || '').toLowerCase();
        const valStr = String(option.value || '').toLowerCase();
        return searchToken.includes(normalized) || labelStr.includes(normalized) || valStr.includes(normalized);
      })
      .slice(0, 30);
  }, [normalized, options]);

  useLayoutEffect(() => {
    if (!open || !inputWrapRef.current) return;
    const updatePosition = () => {
      const rect = inputWrapRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        top: rect.bottom + 4,
        maxHeight: '200px',
        zIndex: 9999
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [open, filteredOptions.length]);

  return (
    <div className="flex flex-col gap-1 w-full">
      {!hideLabel && <label className={masterInputLabel}>{label}</label>}
      <div ref={inputWrapRef} className="relative">
        <input
          value={value}
          disabled={disabled}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => !disabled && setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 200)}
          placeholder={placeholder}
          className={`${masterInput} w-full ${disabled ? 'bg-slate-100 cursor-not-allowed text-slate-400' : ''}`}
        />
      </div>
      {open && typeof document !== 'undefined' && createPortal(
        <div style={dropdownStyle} className="overflow-auto rounded-lg border border-slate-300 bg-white shadow-lg max-h-48">
          {filteredOptions.length > 0 ? filteredOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onSelect(opt); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-[11px] font-bold border-b border-slate-100 hover:bg-emerald-50 text-slate-800 uppercase"
            >
              {renderOption ? renderOption(opt) : opt.label}
            </button>
          )) : <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase">{emptyText}</div>}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Prescription;